/**
 * The four outcomes of "Cut out / change background", and the routing that
 * makes them affordable.
 *
 * The user picks a *result*; this table picks the engine. That split is the
 * whole point of the feature:
 *
 *   - A cut-out, with or without a white backdrop, is a **matte** —
 *     BiRefNet via the REMOVE_BACKGROUND addon. 2 credits, and the subject's
 *     pixels are composited, never regenerated.
 *   - Rebuilding a garment's collar, or erasing props and filling in what was
 *     behind them, needs **generation** — nano-banana via `remove_object`.
 *     8 credits to apply, 2 per preview, 1 for detection.
 *
 * Routing a plain cut-out through the generative op would make the single most
 * common operation four times dearer and start repainting products that only
 * needed cutting out. Hence the `engine` field, and hence the request builders
 * below rather than a component assembling params by hand.
 *
 * Hook-free data plus pure functions, same shape as `removeObjectPresets.ts`
 * (whose presets this reuses for the generative pair) and
 * `src/chat/nudges/promptCatalog.ts`.
 */

import type messages from '../messages';
import {
  RemoveObjectPresetId,
  ServerPreset,
  getRemoveObjectPreset,
  serverPresetFor,
} from './removeObjectPresets';

/** Which backend does the work. */
export type CutoutEngine = 'matte' | 'generative';

export type CutoutOutcomeId = 'transparent' | 'white_studio' | 'ghost_mannequin' | 'clear_clutter';

/** `background` accepted by `/addons/bg_remove` (see filespin/core/bg_remove.py). */
export type MatteBackground = 'transparent' | 'white';

/**
 * Rate-card keys (`ai_credit_rates.model`) an outcome bills against.
 *
 * Keys, not numbers: the rates are sysadmin-editable, so the figures are
 * resolved at render time from `/billing/ai-rates` (`useAiRatesQuery`) rather
 * than baked in here, where a re-price would leave the tiles quoting a stale
 * price. This module stays hook-free — callers pass the resolved map in.
 */
const MATTE_RATE_KEY = 'bgremove';
const GENERATIVE_APPLY_RATE_KEY = 'remove_object';
const GENERATIVE_PREVIEW_RATE_KEY = 'remove_object_preview';
const GENERATIVE_DETECT_RATE_KEY = 'remove_object_detect';

/** `flat` from the rate card: credits per operation, keyed by rate key. */
export type FlatRates = Record<string, number> | undefined;

/** Look up one rate. `undefined` when the card has not loaded or has no row
 *  for the key — callers omit the figure rather than showing a guess. */
const rate = (rates: FlatRates, key: string): number | undefined => rates?.[key];

export type CutoutOutcome = {
  id: CutoutOutcomeId;
  engine: CutoutEngine;
  /** Message keys, resolved by the component — keeps this module intl-free. */
  labelMsg: keyof typeof messages;
  descMsg: keyof typeof messages;
  /** Prefills the save form. */
  defaultResultName: string;

  /** matte only: what the cut-out is placed on. */
  matteBackground?: MatteBackground;
  /** generative only: which `removeObjectPresets` entry drives the prompt. */
  presetId?: RemoveObjectPresetId;
  /**
   * generative only: the backgrounds the user may pick between, first one the
   * default. Only ghost mannequin offers a choice — the same two surfaces the
   * matte tiles speak, transparent and white. Clear-the-clutter keeps the
   * original scene by definition, so it deliberately has no entry here.
   */
  backgroundOptions?: string[];

  /**
   * Whether Adjust's generative-only controls apply.
   *
   * False for a matte, and not as a simplification: the matte cannot erase a
   * prop that overlaps the subject (it only separates subject from background),
   * and free text has nothing to act on when no pixels are repainted. Offering
   * either control there would be a control that quietly does nothing.
   */
  allowsAdjust: boolean;
};

export const CUTOUT_OUTCOMES: CutoutOutcome[] = [
  {
    id: 'transparent',
    engine: 'matte',
    labelMsg: 'cutoutTransparent',
    descMsg: 'cutoutTransparentDesc',
    defaultResultName: 'cutout',
    matteBackground: 'transparent',
    allowsAdjust: false,
  },
  {
    id: 'white_studio',
    engine: 'matte',
    labelMsg: 'cutoutWhiteStudio',
    descMsg: 'cutoutWhiteStudioDesc',
    defaultResultName: 'on_white',
    // Flattened server-side after the matte (one provider call, so still 2
    // credits) rather than asking the generative model for a white backdrop.
    matteBackground: 'white',
    allowsAdjust: false,
  },
  {
    id: 'ghost_mannequin',
    engine: 'generative',
    labelMsg: 'cutoutGhostMannequin',
    descMsg: 'cutoutGhostMannequinDesc',
    defaultResultName: 'ghost',
    presetId: 'ghost_mannequin',
    // Transparent first = the default (matches the preset's own background).
    // The matting call behind it is absorbed into the same 8 credits.
    backgroundOptions: ['transparent', 'white'],
    allowsAdjust: true,
  },
  {
    id: 'clear_clutter',
    engine: 'generative',
    labelMsg: 'cutoutClearClutter',
    descMsg: 'cutoutClearClutterDesc',
    defaultResultName: 'cleaned_up',
    // The in-scene template: keeps the original background, which is what makes
    // this "clear what's around the subject" rather than "isolate the subject".
    presetId: 'erase_people',
    allowsAdjust: true,
  },
];

export const getCutoutOutcome = (id?: string): CutoutOutcome | undefined =>
  CUTOUT_OUTCOMES.find((o) => o.id === id);

export const isMatte = (outcome?: CutoutOutcome): boolean => outcome?.engine === 'matte';

/** Credits to apply. A matte's preview *is* the operation, so nothing more is
 *  charged when the user saves it. */
export const applyCreditsFor = (
  outcome: CutoutOutcome,
  rates?: FlatRates,
): number | undefined =>
  outcome.engine === 'matte' ? 0 : rate(rates, GENERATIVE_APPLY_RATE_KEY);

/** Credits for one preview. This is the whole cost of a matte outcome. */
export const previewCreditsFor = (
  outcome: CutoutOutcome,
  rates?: FlatRates,
): number | undefined =>
  outcome.engine === 'matte'
    ? rate(rates, MATTE_RATE_KEY)
    : rate(rates, GENERATIVE_PREVIEW_RATE_KEY);

/** Credits for the subject/props detection a generative outcome runs once. */
export const detectCreditsFor = (
  outcome: CutoutOutcome,
  rates?: FlatRates,
): number | undefined =>
  outcome.engine === 'matte' ? 0 : rate(rates, GENERATIVE_DETECT_RATE_KEY);

/** Query params for `GET /assets/{id}/addons/bg_remove`. */
export type MatteRequest = { background: MatteBackground };

export function buildMatteRequest(outcome: CutoutOutcome): MatteRequest {
  if (outcome.engine !== 'matte') {
    throw new Error(`${outcome.id} is not a matte outcome`);
  }
  return { background: outcome.matteBackground || 'transparent' };
}

/** What the user may adjust on a generative outcome. */
export type CutoutAdjustments = {
  subject?: string;
  remove?: string[];
  refinePrompt?: string;
  /** Must be one of the outcome's `backgroundOptions`. */
  background?: string;
};

export type GenerativeRequest = {
  operation: 'remove_object';
  params: {
    fidelity: 'presentation';
    preset?: ServerPreset;
    background: string;
    subject?: string;
    remove?: string[];
    refine_prompt?: string;
  };
};

/**
 * Body for `POST /assets/{id}/edit/preview`.
 *
 * Empty optional slots are omitted rather than sent blank, so the server's own
 * defaults apply — the same contract the Remove object panel used.
 */
export function buildGenerativeRequest(
  outcome: CutoutOutcome,
  adjustments: CutoutAdjustments = {},
): GenerativeRequest {
  if (outcome.engine !== 'generative') {
    throw new Error(`${outcome.id} is not a generative outcome`);
  }
  const preset = getRemoveObjectPreset(outcome.presetId);
  const subject = adjustments.subject?.trim();
  const refine = adjustments.refinePrompt?.trim();
  if (
    adjustments.background &&
    !(outcome.backgroundOptions || []).includes(adjustments.background)
  ) {
    // Same stance as the engine guard above: a background this outcome never
    // offered is a wire value smuggled past the menu, not a preference.
    throw new Error(`${outcome.id} does not offer background ${adjustments.background}`);
  }
  return {
    operation: 'remove_object',
    params: {
      fidelity: 'presentation',
      preset: serverPresetFor(outcome.presetId),
      // The user's pick from this outcome's `backgroundOptions` wins; the
      // preset supplies the default (ghost: transparent; in-scene erasure
      // keeps the original).
      background: adjustments.background || preset?.background || 'white',
      ...(subject ? { subject } : {}),
      ...(adjustments.remove?.length ? { remove: adjustments.remove } : {}),
      ...(refine ? { refine_prompt: refine } : {}),
    },
  };
}
